import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Calendar, Users, CheckCircle, XCircle, ArrowLeft, MessageCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { buildWhatsAppLink, trackWhatsAppClick } from "@/components/FloatingWhatsApp";
import { InlineCallButton } from "@/components/CallButton";
import { useAuth } from "@/contexts/AuthContext";
import { trackBeginCheckout } from "@/lib/analytics";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PackageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingData, setBookingData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    travel_date: "",
    num_adults: 1,
    num_children: 0,
    special_requests: "",
  });
  const [submitting, setSubmitting] = useState(false);
  // Referral + credit state
  const [refInput, setRefInput] = useState("");
  const [refValidating, setRefValidating] = useState(false);
  const [refApplied, setRefApplied] = useState(null); // { code, discount_amount_inr, discount_pct, referrer_name }
  const [useCredit, setUseCredit] = useState(false);
  const creditBalance = Number(user?.credit_balance || 0);

  // Auto-fill referral code from ?ref= URL param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) setRefInput(ref.toUpperCase());
  }, []);

  // Pricing calculations
  const totalPax = Number(bookingData.num_adults || 0) + Number(bookingData.num_children || 0);
  const applicableTier = (() => {
    const tiers = pkg?.pricing_tiers || [];
    if (!tiers.length) return null;
    for (const t of tiers) {
      const min = Number(t.min_pax || 1);
      const max = t.max_pax == null ? Infinity : Number(t.max_pax);
      if (totalPax >= min && totalPax <= max) return t;
    }
    return null;
  })();
  const basePrice = applicableTier
    ? Number(applicableTier.price_per_person) * totalPax
    : Number(pkg?.price || 0);
  const discountAmt = refApplied ? Number(refApplied.discount_amount_inr || 0) : 0;
  const priceAfterDiscount = Math.max(basePrice - discountAmt, 0);
  const creditToUse = useCredit ? Math.min(creditBalance, priceAfterDiscount) : 0;
  const finalTotal = Math.max(priceAfterDiscount - creditToUse, 0);

  const applyReferralCode = async () => {
    if (!refInput.trim()) return;
    setRefValidating(true);
    try {
      const res = await axios.post(`${API}/booking/apply-referral`, {
        code: refInput.trim().toUpperCase(),
        package_id: pkg.id,
        num_adults: Number(bookingData.num_adults || 1),
        num_children: Number(bookingData.num_children || 0),
      });
      setRefApplied(res.data);
      toast.success(`Referral applied! ${res.data.discount_pct}% off — ₹${res.data.discount_amount_inr.toLocaleString('en-IN')} saved.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid referral code");
      setRefApplied(null);
    } finally {
      setRefValidating(false);
    }
  };

  const removeReferralCode = () => {
    setRefApplied(null);
    setRefInput("");
  };

  useEffect(() => {
    fetchPackage();
  }, [id]);

  const fetchPackage = async () => {
    try {
      const response = await axios.get(`${API}/packages/${id}`);
      setPkg(response.data);
    } catch (error) {
      console.error("Error fetching package:", error);
      toast.error("Package not found");
      navigate("/packages");
    } finally {
      setLoading(false);
    }
  };

  const handleBookingChange = (e) => {
    const { name, value } = e.target;
    setBookingData({ ...bookingData, [name]: value });
  };

  const createBooking = async () => {
    const bookingResponse = await axios.post(`${API}/bookings`, {
      ...bookingData,
      package_id: pkg.id,
      referral_code_used: refApplied ? refApplied.code : null,
      credit_used: creditToUse,
    });
    const booking = bookingResponse.data.booking;
    // GA4 e-commerce: begin_checkout
    trackBeginCheckout({
      bookingId: booking.id,
      packageId: pkg.id,
      packageName: pkg.name,
      amountInr: booking.total_amount,
      category: pkg.category,
      quantity: (parseInt(bookingData.num_adults, 10) || 1) + (parseInt(bookingData.num_children, 10) || 0),
    });
    return booking;
  };

  const handleStripePayment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const booking = await createBooking();
      const checkoutResponse = await axios.post(`${API}/checkout/session`, {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkoutResponse.data.url;
    } catch (error) {
      console.error("Stripe error:", error);
      toast.error("Failed to start Stripe payment. Please try again.");
      setSubmitting(false);
    }
  };

  const handleRazorpayPayment = async (e) => {
    e.preventDefault();

    // Validate form fields manually since we're not using form submit
    if (!bookingData.customer_name || !bookingData.customer_email || !bookingData.customer_phone || !bookingData.travel_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!window.Razorpay) {
      toast.error("Razorpay is loading, please try again in a moment");
      return;
    }

    setSubmitting(true);
    try {
      const booking = await createBooking();
      const orderRes = await axios.post(`${API}/razorpay/create-order`, {
        booking_id: booking.id,
      });

      const options = {
        key: orderRes.data.key_id,
        amount: orderRes.data.amount,
        currency: orderRes.data.currency,
        name: "Andaman Avalon",
        description: `${pkg.name} - ${pkg.duration}`,
        image: "https://customer-assets-jai6qajn.emergentagent.net/job_031c01f6-1122-4ae8-87ee-69acab3ae0f1/artifacts/kw4zjnmy_ava.jpeg",
        order_id: orderRes.data.order_id,
        prefill: {
          name: orderRes.data.booking.customer_name,
          email: orderRes.data.booking.customer_email,
          contact: orderRes.data.booking.customer_phone,
        },
        theme: { color: "#0A192F" },
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(`${API}/razorpay/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              booking_id: booking.id,
            });
            toast.success("Payment successful! Redirecting...");
            setTimeout(() => {
              navigate(`/payment-success?session_id=${response.razorpay_order_id}&provider=razorpay`);
            }, 800);
          } catch (err) {
            toast.error("Payment verification failed. Please contact support.");
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment cancelled");
            setSubmitting(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        toast.error(`Payment failed: ${response.error.description || "Unknown error"}`);
        setSubmitting(false);
      });
      rzp.open();
    } catch (error) {
      console.error("Razorpay error:", error);
      const msg = error.response?.data?.detail || "Failed to start Razorpay payment";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!pkg) {
    return null;
  }

  return (
    <div data-testid="package-detail-page" className="grain-texture min-h-screen">
      <SEO
        title={`${pkg.name} - ${pkg.duration} Andaman Tour Package`}
        description={`${pkg.description?.slice(0, 155) || `Book the ${pkg.name} Andaman tour - ${pkg.duration} starting at ₹${pkg.price?.toLocaleString('en-IN')}.`}`}
        path={`/packages/${pkg.id}`}
        image={pkg.image_url}
        type="product"
        keywords={`${pkg.name}, ${pkg.category} andaman tour, ${pkg.duration} andaman package, andaman ${pkg.category?.toLowerCase()} package`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: pkg.name,
          description: pkg.description,
          image: pkg.image_url,
          brand: { "@type": "Brand", name: "Andaman Avalon" },
          category: pkg.category,
          offers: {
            "@type": "Offer",
            price: pkg.price,
            priceCurrency: "INR",
            availability: "https://schema.org/InStock",
            url: `https://www.andamanavalon.com/packages/${pkg.id}`,
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "42",
          },
        }}
      />
      <Navbar />

      {/* Hero Image */}
      <section className="relative h-[60vh] mt-20">
        <img
          src={pkg.image_url}
          alt={pkg.name}
          className="w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <span className="accent-label text-secondary mb-4 block">{pkg.category}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold mb-4">
              {pkg.name}
            </h1>
            <p className="text-lg">{pkg.duration}</p>
          </div>
        </div>
      </section>

      {/* Package Details */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h2 className="text-3xl font-serif mb-4">Package Overview</h2>
                <p className="text-muted-foreground leading-relaxed">{pkg.description}</p>
              </motion.div>

              {/* Highlights */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                data-testid="highlights-section"
              >
                <h2 className="text-3xl font-serif mb-4">Highlights</h2>
                <ul className="space-y-2">
                  {pkg.highlights.map((highlight, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-secondary mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Itinerary */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                data-testid="itinerary-section"
              >
                <h2 className="text-3xl font-serif mb-6">Day-by-Day Itinerary</h2>
                <div className="space-y-4">
                  {pkg.itinerary.map((day, index) => (
                    <div key={index} className="admin-card">
                      <h3 className="text-xl font-serif mb-2">
                        Day {day.day}: {day.title}
                      </h3>
                      <p className="text-muted-foreground">{day.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Group Pricing Tiers */}
              {pkg.pricing_tiers && pkg.pricing_tiers.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  data-testid="pricing-tiers-section"
                  className="admin-card"
                >
                  <h2 className="text-2xl font-serif mb-2">Group Pricing</h2>
                  <p className="text-sm text-muted-foreground mb-4">The more you are, the less each person pays.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-left py-2 px-3">Group Size</th>
                          <th className="text-right py-2 px-3">Per Person</th>
                          <th className="text-right py-2 px-3">Savings</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pkg.pricing_tiers.map((t, i) => {
                          const min = Number(t.min_pax || 1);
                          const max = t.max_pax == null || t.max_pax === "" ? null : Number(t.max_pax);
                          const label = max == null ? `${min}+ pax` : `${min} – ${max} pax`;
                          const perPerson = Number(t.price_per_person || 0);
                          const savings = i === 0 ? 0 : Number(pkg.pricing_tiers[0].price_per_person) - perPerson;
                          return (
                            <tr key={i} className="border-b hover:bg-muted/20">
                              <td className="py-2 px-3 font-medium">{label}</td>
                              <td className="py-2 px-3 text-right font-semibold">₹{perPerson.toLocaleString('en-IN')}</td>
                              <td className="py-2 px-3 text-right text-green-700">
                                {savings > 0 ? `Save ₹${savings.toLocaleString('en-IN')} / pax` : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}

              {/* Inclusions & Exclusions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-8"
              >
                <div data-testid="inclusions-section">
                  <h2 className="text-2xl font-serif mb-4">Inclusions</h2>
                  <ul className="space-y-2">
                    {pkg.inclusions.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div data-testid="exclusions-section">
                  <h2 className="text-2xl font-serif mb-4">Exclusions</h2>
                  <ul className="space-y-2">
                    {pkg.exclusions.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <XCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </div>

            {/* Booking Card */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="admin-card sticky top-24"
                data-testid="booking-card"
              >
                  <div className="mb-6">
                  <span className="text-4xl font-bold text-primary">₹{pkg.price.toLocaleString('en-IN')}</span>
                  <span className="text-muted-foreground ml-2">per person</span>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center text-muted-foreground">
                    <Calendar className="w-5 h-5 mr-2" />
                    <span>{pkg.duration}</span>
                  </div>
                  <div className="flex items-center text-muted-foreground">
                    <Users className="w-5 h-5 mr-2" />
                    <span>Suitable for all ages</span>
                  </div>
                </div>

                <Button
                  data-testid="book-now-btn"
                  onClick={() => {
                    if (!isAuthenticated) {
                      toast.info("Please sign in to book this package");
                      navigate("/login", { state: { from: `/packages/${id}` } });
                      return;
                    }
                    // Prefill from user profile
                    setBookingData((prev) => ({
                      ...prev,
                      customer_name: user?.name || prev.customer_name,
                      customer_email: user?.email || prev.customer_email,
                      customer_phone: user?.phone || prev.customer_phone,
                    }));
                    setShowBookingModal(true);
                  }}
                  className="w-full bg-primary hover:bg-primary/90 h-12 text-lg"
                >
                  Book Now
                </Button>

                <a
                  href={buildWhatsAppLink(`Hi Andaman Avalon! I'm interested in the "${pkg.name}" package (${pkg.duration}, ₹${pkg.price.toLocaleString('en-IN')}). Can you share more details?`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-book-btn"
                  onClick={() => trackWhatsAppClick("package_detail", pkg.name)}
                  className="w-full mt-3 inline-flex items-center justify-center h-12 rounded-md bg-[#25D366] hover:bg-[#20BA5A] text-white font-medium transition-colors"
                >
                  <MessageCircle size={20} className="mr-2 fill-white" />
                  Book via WhatsApp
                </a>

                <InlineCallButton packageName={pkg.name} />

                <Button
                  data-testid="back-to-packages-btn"
                  onClick={() => navigate("/packages")}
                  variant="outline"
                  className="w-full mt-4"
                >
                  <ArrowLeft className="mr-2" size={18} />
                  Back to Packages
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif">Complete Your Booking</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Full Name *</label>
                <Input
                  name="customer_name"
                  data-testid="booking-name"
                  value={bookingData.customer_name}
                  onChange={handleBookingChange}
                  required
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <Input
                  name="customer_email"
                  type="email"
                  data-testid="booking-email"
                  value={bookingData.customer_email}
                  onChange={handleBookingChange}
                  required
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Phone *</label>
                <Input
                  name="customer_phone"
                  data-testid="booking-phone"
                  value={bookingData.customer_phone}
                  onChange={handleBookingChange}
                  required
                  placeholder="+91 1234567890"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Travel Date *</label>
                <Input
                  name="travel_date"
                  type="date"
                  data-testid="booking-date"
                  value={bookingData.travel_date}
                  onChange={handleBookingChange}
                  required
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Number of Adults *</label>
                <Input
                  name="num_adults"
                  type="number"
                  data-testid="booking-adults"
                  value={bookingData.num_adults}
                  onChange={handleBookingChange}
                  required
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Number of Children</label>
                <Input
                  name="num_children"
                  type="number"
                  data-testid="booking-children"
                  value={bookingData.num_children}
                  onChange={handleBookingChange}
                  min="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Special Requests</label>
              <Textarea
                name="special_requests"
                data-testid="booking-requests"
                value={bookingData.special_requests}
                onChange={handleBookingChange}
                rows={4}
                placeholder="Any special requirements or requests..."
              />
            </div>

            {/* Referral code input */}
            <div className="border-t pt-4" data-testid="booking-referral-block">
              <label className="block text-sm font-medium mb-2">Referral code (optional)</label>
              {refApplied ? (
                <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md p-3" data-testid="referral-applied-banner">
                  <div>
                    <div className="text-sm font-semibold text-green-800">
                      Code {refApplied.code} applied — {refApplied.discount_pct}% off
                    </div>
                    <div className="text-xs text-green-700">
                      Saved ₹{Number(refApplied.discount_amount_inr).toLocaleString('en-IN')} · thanks to {refApplied.referrer_name || 'a friend'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeReferralCode}
                    data-testid="referral-remove-btn"
                    className="text-xs text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    data-testid="referral-code-input"
                    value={refInput}
                    onChange={(e) => setRefInput(e.target.value.toUpperCase())}
                    placeholder="e.g. SAHIL1234"
                    className="uppercase"
                  />
                  <Button
                    type="button"
                    onClick={applyReferralCode}
                    disabled={refValidating || !refInput.trim()}
                    data-testid="referral-apply-btn"
                    variant="outline"
                  >
                    {refValidating ? "Checking…" : "Apply"}
                  </Button>
                </div>
              )}
            </div>

            {/* Credit balance toggle */}
            {creditBalance > 0 && (
              <div className="border-t pt-4" data-testid="booking-credit-block">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="use-credit-toggle"
                    checked={useCredit}
                    onChange={(e) => setUseCredit(e.target.checked)}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium">
                      Apply my Andaman Avalon credit — ₹{creditBalance.toLocaleString('en-IN')} available
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {useCredit
                        ? `Using ₹${creditToUse.toLocaleString('en-IN')} towards this booking`
                        : "Toggle to apply your earned referral credit"}
                    </div>
                  </div>
                </label>
              </div>
            )}

            <div className="bg-muted p-4 rounded-lg space-y-2" data-testid="booking-summary">
              {applicableTier ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span>Per-person rate ({totalPax} pax)</span>
                    <span>₹{Number(applicableTier.price_per_person).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Subtotal ({totalPax} × ₹{Number(applicableTier.price_per_person).toLocaleString('en-IN')})</span>
                    <span>₹{basePrice.toLocaleString('en-IN')}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between text-sm">
                  <span>Package price</span>
                  <span>₹{basePrice.toLocaleString('en-IN')}</span>
                </div>
              )}
              {discountAmt > 0 && (
                <div className="flex justify-between text-sm text-green-700" data-testid="summary-referral-discount">
                  <span>Referral discount ({refApplied.discount_pct}%)</span>
                  <span>− ₹{discountAmt.toLocaleString('en-IN')}</span>
                </div>
              )}
              {creditToUse > 0 && (
                <div className="flex justify-between text-sm text-green-700" data-testid="summary-credit-used">
                  <span>Credit applied</span>
                  <span>− ₹{creditToUse.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-lg font-medium">Total Amount:</span>
                <span className="text-2xl font-bold text-primary" data-testid="booking-total">₹{finalTotal.toLocaleString('en-IN')}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Choose your preferred payment method below
              </p>
            </div>

            {/* Payment Method Buttons */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-muted-foreground">Select Payment Method:</p>

              {/* Razorpay - Recommended for Indian customers */}
              <button
                type="button"
                onClick={handleRazorpayPayment}
                disabled={submitting}
                data-testid="pay-razorpay-btn"
                className="w-full flex items-center justify-between p-4 rounded-lg border-2 border-primary bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-bold text-lg">R</span>
                  </div>
                  <div className="text-left">
                    <div className="font-semibold flex items-center gap-2">
                      Pay with Razorpay
                      <span className="text-[10px] bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded font-bold">RECOMMENDED</span>
                    </div>
                    <div className="text-xs opacity-80">UPI, Cards, Net Banking, Wallets · ₹{pkg.price.toLocaleString('en-IN')}</div>
                  </div>
                </div>
                <ArrowLeft className="rotate-180 group-hover:translate-x-1 transition-transform" size={20} />
              </button>

              {/* Stripe - For international customers */}
              <button
                type="button"
                onClick={handleStripePayment}
                disabled={submitting}
                data-testid="pay-stripe-btn"
                className="w-full flex items-center justify-between p-4 rounded-lg border-2 border-border bg-white hover:border-primary hover:bg-muted/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#635BFF] rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">S</span>
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-primary">Pay with Stripe</div>
                    <div className="text-xs text-muted-foreground">International Cards · ~US${(pkg.price / 83).toFixed(2)}</div>
                  </div>
                </div>
                <ArrowLeft className="rotate-180 text-muted-foreground group-hover:translate-x-1 group-hover:text-primary transition-all" size={20} />
              </button>

              {submitting && (
                <p className="text-center text-sm text-muted-foreground animate-pulse">Processing your payment...</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default PackageDetail;
